import { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  fetchProfile,
  // fetchPaymentHistory,
  profileSelector,
  clearSuccess,
  clearError,
} from "./profileSlice";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Edit2 } from "lucide-react";
import { motion } from "framer-motion";
import { EditProfileDialog } from "./EditProfileDialog";
import { toast } from "sonner";
import "./profile.scss";

const Profile = () => {
  const dispatch = useAppDispatch();
  const { profile, isFetching, isSuccess, isError, errorMessage, successMessage } =
    useAppSelector(profileSelector);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [, setWeeklyReports] = useState(true);
  const [, setFeatureAnnouncements] = useState(true);
  const hasFetched = useRef(false);

  // Fetch profile and payment history on mount
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(fetchProfile());
    // dispatch(fetchPaymentHistory());
  }, [dispatch]);

  // Initialize notification preferences from profile
  useEffect(() => {
    if (profile) {
      setWeeklyReports(profile.weekly_progress_reports ?? true);
      setFeatureAnnouncements(profile.new_feature_announcements ?? true);
    }
  }, [profile]);

  // Handle success/error messages
  useEffect(() => {
    if (isSuccess) {
      toast.success(successMessage || "Update successful!");
      dispatch(clearSuccess());
    }
    if (isError && errorMessage) {
      toast.error(errorMessage);
      dispatch(clearError());
    }
  }, [isSuccess, successMessage, isError, errorMessage, dispatch]);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };



  if (isFetching && !profile) {
    return (
      <div className="min-h-screen bg-white py-12 px-4">
        <div className="max-w-5xl mx-auto space-y-12">
          <div className="text-center space-y-4">
            <Skeleton className="h-12 w-64 mx-auto" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <Skeleton className="h-48 w-full rounded-[2.5rem]" />
          <div className="grid grid-cols-2 gap-8">
            <Skeleton className="h-64 w-full rounded-3xl" />
            <Skeleton className="h-64 w-full rounded-3xl" />
          </div>
        </div>
      </div>
    );
  }

  const preferredSubjects: string[] = profile?.preferred_subjects || ["GS1", "GS2", "GS3", "GS4"];

  return (
    <div className="min-h-screen bg-white text-gray-900 py-16 px-4 md:px-8 font-sans">
      <div className="max-w-6xl mx-auto">

        {/* Header Section - Matches screenshot typography */}
        <div className="text-center mb-16 relative">
          {/* Subtle background element */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-64 bg-slate-50/50 rounded-full blur-3xl -z-10" />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block relative mb-6">
              <Avatar className="h-32 w-32 border-4 border-white shadow-xl shadow-slate-200/50">
                <AvatarImage src={profile?.avatar || undefined} className="object-cover" />
                <AvatarFallback className="text-4xl font-bold bg-slate-900 text-white">
                  {getInitials(profile?.full_name)}
                </AvatarFallback>
              </Avatar>
              <button
                onClick={() => setIsDialogOpen(true)}
                className="absolute bottom-0 right-0 p-2.5 bg-black text-white rounded-full hover:scale-110 transition-transform shadow-lg border-2 border-white"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-4 leading-tight">
              On a mission to clear<br /> <span className="text-indigo-500">UPSC {profile?.target_exam_year || "2025"}</span>
            </h1>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
              Welcome back, <span className="font-semibold text-slate-900">{profile?.full_name}</span>. You are creating a personalized learning journey driven by data and consistent effort.
            </p>
          </motion.div>
        </div>


        {/* Stats Card - Matches screenshot card design */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-24"
        >
          <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/40 border border-slate-100 p-8 md:p-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-0 divide-x-0 lg:divide-x divide-slate-100">

              <div className="text-center md:px-6">
                <p className="text-sm font-medium text-slate-400 uppercase tracking-widest mb-3">Joined</p>
                <p className="text-4xl md:text-5xl font-bold text-slate-900">
                  {profile?.created_at ? new Date(profile.created_at).getFullYear() : new Date().getFullYear()}
                </p>
              </div>

              <div className="text-center md:px-6">
                <p className="text-sm font-medium text-slate-400 uppercase tracking-widest mb-3">Target Year</p>
                <p className="text-4xl md:text-5xl font-bold text-slate-900">{profile?.target_exam_year || "2025"}</p>
              </div>

              <div className="text-center md:px-6">
                <p className="text-sm font-medium text-slate-400 uppercase tracking-widest mb-3">Focus Subjects</p>
                <p className="text-4xl md:text-5xl font-bold text-slate-900">{preferredSubjects.length}</p>
              </div>

              <div className="text-center md:px-6">
                <p className="text-sm font-medium text-slate-400 uppercase tracking-widest mb-3">Status</p>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-green-700 font-bold text-lg md:text-xl align-middle mt-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                  Active
                </div>
              </div>

            </div>
          </div>
        </motion.div>


        {/* Content Sections - Centered Editorial Layout */}
        <div className="flex flex-col items-center justify-center px-4 mb-24 max-w-4xl mx-auto">

          {/* Centered Mission/Subjects */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-8 leading-tight">
              Dedicated to Data-Driven<br />Preparation.
            </h2>
            <p className="text-xl text-slate-500 mb-12 leading-relaxed max-w-2xl mx-auto">
              Your preparation is unique. We are driven to transform the landscape of your learning into a captivating journey of excitement and discovery.
            </p>

            <div className="space-y-8">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest">Your Focus Areas</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {preferredSubjects.map((subject) => (
                  <span key={subject} className="px-6 py-3 rounded-full bg-indigo-500 text-white font-bold text-sm border border-indigo-100 shadow-md shadow-indigo-200">
                    {subject}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column: Settings/Preferences
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-8"
          >
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-2xl font-bold text-slate-900">Preferences</h3>
              <button onClick={handleSignOut} className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex items-start justify-between group p-4 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => {
                setWeeklyReports(!weeklyReports);
                dispatch(updateNotificationPreferences({ weekly_progress_reports: !weeklyReports, new_feature_announcements: featureAnnouncements }));
              }}>
                <div>
                  <Label className="text-lg font-semibold text-slate-900 cursor-pointer">Weekly Reports</Label>
                  <p className="text-slate-500 mt-1">Get a summary of your writing analysis every Monday.</p>
                </div>
                <div className={`w-12 h-7 rounded-full transition-colors relative ${weeklyReports ? 'bg-black' : 'bg-slate-200'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-all ${weeklyReports ? 'left-6' : 'left-1'}`} />
                </div>
              </div>

              <div className="flex items-start justify-between group p-4 rounded-2xl hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => {
                setFeatureAnnouncements(!featureAnnouncements);
                dispatch(updateNotificationPreferences({ weekly_progress_reports: weeklyReports, new_feature_announcements: !featureAnnouncements }));
              }}>
                <div>
                  <Label className="text-lg font-semibold text-slate-900 cursor-pointer">Product Updates</Label>
                  <p className="text-slate-500 mt-1">Be the first to know about new features on Parakh.</p>
                </div>
                <div className={`w-12 h-7 rounded-full transition-colors relative ${featureAnnouncements ? 'bg-black' : 'bg-slate-200'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-all ${featureAnnouncements ? 'left-6' : 'left-1'}`} />
                </div>
              </div>

              <div className="p-6 bg-slate-900 rounded-3xl text-white mt-8 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/30 rounded-full blur-3xl -mr-10 -mt-10 group-hover:bg-indigo-500/40 transition-colors" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <CreditCard className="w-5 h-5 text-indigo-300" />
                    <span className="font-semibold text-indigo-200 uppercase tracking-wider text-xs">Membership</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Standard Plan</h3>
                  <p className="text-slate-400 mb-6 text-sm">Valid until Dec 2025</p>
                  <button className="w-full bg-white text-black font-bold py-3 rounded-xl hover:bg-slate-100 transition-colors text-sm">
                    Manage Subscription
                  </button>
                </div>
              </div>
            </div>

          </motion.div> */}

      </div>

        {/* Payment History Section */}
        {/* {paymentHistory && paymentHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-24 max-w-4xl mx-auto"
          >
            <h3 className="text-center text-sm font-bold text-slate-900 uppercase tracking-widest mb-8">Payment History</h3>
            <div className="bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/40 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="p-6 text-sm font-semibold text-slate-500 uppercase tracking-wider">Plan</th>
                      <th className="p-6 text-sm font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="p-6 text-sm font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                      <th className="p-6 text-sm font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paymentHistory.map((payment: { payment_id: Key | null | undefined; plan_name: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined; payment_date: string | number | Date; amount: number; status: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | null | undefined; }) => (
                      <tr key={payment.payment_id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-6 font-medium text-slate-900">{payment.plan_name}</td>
                        <td className="p-6 text-slate-600">
                          {new Date(payment.payment_date).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td className="p-6 font-semibold text-slate-900">₹{payment.amount}</td>
                        <td className="p-6">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                            payment.status === 'success' || payment.status === 'captured'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {payment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )} */}

     



  <EditProfileDialog
    open={isDialogOpen}
    onOpenChange={setIsDialogOpen}
    profile={profile}
    />
    </div >
  );
};

export default Profile;
